// What the navigation shows — decided ONCE, drawn three ways.
//
// The top bar's menus, the phone drawer and the Home page all draw from
// buildNavEntries(): each visible sidebar item as a `menu` (a tree that
// opens) or a `link` (an address), with the same group filtering and the
// same admin-mode rule for Settings. An item added to the navigation cannot
// appear in one and be missing from another, and a person never sees on
// Home a page the bar would hide from them.
//
// Pure: no React, no fetch. `resolveUrl` is passed in (navUrls.resolveNavUrl
// against this origin) so the rule for "Stride page vs web address" stays
// in one file.

// The sidebar config used before /api/sidebar answers (or when it fails).
export const DEFAULT_SIDEBAR_CONFIG = [
  { id: 'navigation', type: 'builtin' },
  { id: 'zebra-simple-dashboards', type: 'builtin' },
  { id: 'settings', type: 'builtin' },
];

function visibleTo(item, userGroups) {
  if (!item.restrictedGroups || item.restrictedGroups.length === 0) return true;
  if (!userGroups) return false;
  return item.restrictedGroups.some((g) => userGroups.includes(g));
}

// Recursively drop items the user's groups can't see. An item with no
// restrictedGroups is visible to everyone.
export function filterByGroups(items, userGroups) {
  if (!items) return [];
  return items
    .filter((item) => visibleTo(item, userGroups))
    .map((item) => ({
      ...item,
      children: item.children ? filterByGroups(item.children, userGroups) : [],
    }));
}

export function buildNavEntries({ sidebarConfig, navTree, userGroups, adminMode, resolveUrl, isExternal }) {
  const visibleItems = (sidebarConfig || []).filter((item) => {
    if (!visibleTo(item, userGroups)) return false;
    if (item.type === 'builtin' && item.id === 'settings' && !adminMode) return false;
    return true;
  });
  return visibleItems.map((item) => {
    if (item.type === 'builtin' && item.id === 'navigation') {
      return { id: item.id, kind: 'menu', label: item.label || 'Navigation', item, iconFallback: '🧭', items: filterByGroups(navTree, userGroups), emptyText: 'No navigation items configured' };
    }
    if (item.type === 'builtin' && item.id === 'zebra-simple-dashboards') {
      const label = item.label || 'Dashboards';
      return { id: item.id, kind: 'link', label, tabLabel: label, item: { ...item, icon: item.icon || 'zsd' }, iconFallback: '📊', url: '/dashboards', external: false };
    }
    if (item.type === 'builtin' && item.id === 'settings') {
      return { id: item.id, kind: 'link', label: item.label || 'Settings', tabLabel: 'Settings', item, iconFallback: '⚙️', url: '/settings', external: false };
    }
    if (item.type === 'custom' && item.style === 'menu') {
      return { id: item.id, kind: 'menu', label: item.label, item, iconFallback: '📋', items: filterByGroups(item.menuTree || [], userGroups), emptyText: 'No items in this menu' };
    }
    // Custom link (default)
    const url = resolveUrl(item.url);
    return { id: item.id, kind: 'link', label: item.label, tabLabel: item.label, item, iconFallback: '🔗', url, external: isExternal(url) };
  });
}

// A menu's tree as Home lays it out: ONE sub-section per item the menu
// shows, in the menu's own order — exactly the rows its dropdown in the bar
// lists. An item that is a page is a sub-section holding that one tile; an
// item that opens a sub-menu holds a tile per item of that sub-menu, in its
// order. A deeper level never changes that layout: a tile whose item opens
// a sub-menu of its own carries it as `children` (tiles again, as deep as
// the tree goes), which Home shows only while that tile is hovered. Pages
// without an address are dropped, and a sub-menu with no page anywhere
// below it is not drawn.
const hasChildren = (node) => !!(node && Array.isArray(node.children) && node.children.length > 0);
const keyOf = (node, i) => (node.id != null ? String(node.id) : `${i}:${node.label || ''}`);

// → [{ id, item, url, children }]: a page has its url and no children; a
// sub-menu has no url (in the bar too, a parent does not navigate).
export function homeTilesOf(children, resolveUrl) {
  const tiles = [];
  (children || []).forEach((node, i) => {
    const id = keyOf(node, i);
    if (hasChildren(node)) {
      const inner = homeTilesOf(node.children, resolveUrl);
      if (inner.length) tiles.push({ id, item: node, url: null, children: inner });
      return;
    }
    const url = resolveUrl(node.url);
    if (url) tiles.push({ id, item: node, url, children: [] });
  });
  return tiles;
}

// → [{ id, item, label, tiles }], one per item the dropdown lists.
export function homeGroupsOf(items, resolveUrl) {
  const groups = [];
  (items || []).forEach((node, i) => {
    const id = keyOf(node, i);
    if (hasChildren(node)) {
      const tiles = homeTilesOf(node.children, resolveUrl);
      if (tiles.length) groups.push({ id, item: node, label: node.label, tiles });
    } else {
      const url = resolveUrl(node.url);
      if (url) groups.push({ id, item: node, label: node.label, tiles: [{ id, item: node, url, children: [] }] });
    }
  });
  return groups;
}
